import {
  ComponentMultiStyleConfig,
  FormControl,
  FormErrorMessage,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import React, { ReactElement, useMemo } from "react";
import { ComponentStyleProps } from "../../../../lib/styles/props/component-style-props";
import { hasValue } from "../../../../lib/util/common-util";
import { CMFormInputBaseDate } from "../../../control";
import { CMButtonFormCommentProps } from "../../comment";
import { CMFormControlHeader } from "../CMFormControlHeader/CMFormControlHeader";
import { FormRequiredLabelProps } from "../../label";
import { ChangeBadgeProperties } from "../../../../lib";
import { ymdToTextOrHyphen } from "../../../../lib/util/common-date-util";

export type CMFormInputDateProps = {
  editMode?: "editable" | "disabled" | "readOnly";
  size?: "sm" | "md" | "lg";

  /**
   * trueなら、ヘッダなしとなる。
   * この場合、changeBadgePropertiesやcommentButtonPropsが存在しても、変更やコメントを扱わない。
   */
  noHeader?: boolean;

  label?: string;
  placeholder?: string;
  icon?: ReactElement;

  /**
   * 値。
   */
  value?: { year: number; month: number; day: number } | null;

  /**
   * 選択可能な一番古い日付。
   */
  minDate?: { year: number; month: number; day: number };

  /**
   * 選択可能な一番新しい日付。
   */
  maxDate?: { year: number; month: number; day: number };

  /**
   * 必須マークの設定。
   * この項目が設定されている場合は必須ラベルを表示する。
   */
  requiredLabelProps?: FormRequiredLabelProps;

  /**
   * 変更バッジのprops。
   * 値があれば、このコントロールは差分モード(差分があれば変更バッジとオレンジ枠を表示するモード)となる。
   */
  changeBadgeProperties?: ChangeBadgeProperties<{
    year: number;
    month: number;
    day: number;
  }>;

  /**
   * エラーメッセージ。
   * 値があれば、このコントロールはエラー状態となる。
   */
  errorMessage?: string;

  /**
   * コメントボタンのprops。
   * これが存在すれば、コメントボタンを表示する。
   */
  commentButtonProps?: CMButtonFormCommentProps;

  onChange?: (
    value: { year: number; month: number; day: number } | null,
  ) => void;
} & ComponentStyleProps;

/**
 * 日付を表示するコンポーネント
 */
export const CMFormInputDate = ({
  editMode = "editable",
  size = "md",

  noHeader = false,

  label,
  icon,
  placeholder,
  minDate,
  maxDate,
  value = null,

  requiredLabelProps,

  changeBadgeProperties,
  errorMessage,

  commentButtonProps,

  onChange,

  sx,
  ...rest
}: CMFormInputDateProps) => {
  const styles = useMultiStyleConfig("CMFormInputDateStyles");

  const isDiffMode = useMemo(
    () => hasValue(changeBadgeProperties),
    [changeBadgeProperties],
  );

  const isError = hasValue(errorMessage);

  return (
    <FormControl
      isInvalid={isError}
      sx={{ ...styles.container, ...sx }}
      {...rest}
    >
      {!noHeader && (
        <CMFormControlHeader<{ year: number; month: number; day: number }>
          label={label}
          value={value}
          requiredLabelProps={requiredLabelProps}
          changeBadgeProperties={changeBadgeProperties}
          commentButtonProps={commentButtonProps}
          toDisplayValue={ymdToTextOrHyphen}
          sx={styles.header}
        />
      )}
      <CMFormInputBaseDate
        editMode={editMode}
        variant={isError ? "error" : isDiffMode ? "diff" : "default"}
        size={size}
        icon={icon}
        placeholder={placeholder}
        value={value}
        changedFrom={changeBadgeProperties?.changedFrom}
        minDate={minDate}
        maxDate={maxDate}
        onChange={onChange}
        sx={styles.input}
      />
      {isError && editMode !== "readOnly" && (
        <FormErrorMessage sx={styles.error}>{errorMessage}</FormErrorMessage>
      )}
    </FormControl>
  );
};

export const CMFormInputDateStyles: ComponentMultiStyleConfig = {
  parts: ["container", "header", "input", "error"],
  baseStyle: {
    container: {},
    header: {
      mb: "2px",
    },
    input: {},
    error: {
      mt: "4px",
    },
  },
};
